import { ComponentType, lazy } from "react";

// Amplify replaces every file on each deploy, so a tab opened before a deploy
// asks for chunk files that no longer exist the first time it navigates to a
// page it has not loaded yet. When that happens, reload the page once to pick
// up the new build. A sessionStorage flag per import makes sure it can't loop:
// if the same chunk still fails after the reload, the error is rethrown (to
// the error boundary). The flag is cleared once that import loads again.

const FLAG_PREFIX = "lazyWithRetry:";

export const isChunkLoadError = (error: unknown): boolean => {
	if (!error || typeof error !== "object") return false;
	const { name, message } = error as { name?: unknown; message?: unknown };
	if (name === "ChunkLoadError") return true;
	return (
		typeof message === "string" &&
		/Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(
			message
		)
	);
};

// A short stable key per import function: its source names the chunk it loads
// (in production e.g. `()=>n.e(123).then(n.bind(n,456))`).
const flagKey = (factory: () => unknown): string => {
	const source = String(factory);
	let hash = 0;
	for (let i = 0; i < source.length; i++) {
		hash = (hash * 31 + source.charCodeAt(i)) | 0;
	}
	return FLAG_PREFIX + (hash >>> 0).toString(36);
};

const readFlag = (key: string): boolean | null => {
	try {
		return window.sessionStorage.getItem(key) !== null;
	} catch {
		return null; // storage unavailable: can't guard a reload
	}
};

const setFlag = (key: string): boolean => {
	try {
		window.sessionStorage.setItem(key, "1");
		return true;
	} catch {
		return false;
	}
};

const clearFlag = (key: string) => {
	try {
		window.sessionStorage.removeItem(key);
	} catch {
		// nothing to clear
	}
};

// Runs a dynamic import; on a chunk-load failure reloads the page once
// (the returned promise then never settles, so the Suspense fallback stays up
// until the page goes away). Any other error is rethrown.
export const importWithRetry = <T>(
	factory: () => Promise<T>,
	reload: () => void = () => window.location.reload()
): Promise<T> => {
	const key = flagKey(factory);
	return factory().then(
		(module) => {
			clearFlag(key);
			return module;
		},
		(error: unknown) => {
			if (!isChunkLoadError(error) || readFlag(key) !== false || !setFlag(key)) {
				throw error;
			}
			reload();
			return new Promise<T>(() => {});
		}
	);
};

// Drop-in replacement for React.lazy(() => import(...)). Prefetch calls should
// keep using the bare import function (with .catch) so a failed prefetch stays
// silent and never reloads.
export const lazyWithRetry = <T extends ComponentType<any>>(
	factory: () => Promise<{ default: T }>
) => lazy(() => importWithRetry(factory));
